import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Eye, Undo2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg, fmtDT } from "../lib/api";
import { StatusBadge, Countdown, Empty, LeadsTable, LeadDetail, FundDialog, Pager } from "./common";

function BatchLeads({ batch, onClose }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [fund, setFund] = useState(null);
  const load = async (p = page) => {
    try { setData((await api.get(`/batches/${batch.id}/leads`, { params: { page: p } })).data); setPage(p); } catch (e) { toast.error(errMsg(e)); onClose(); }
  };
  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={!!batch} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="batch-leads-dialog">
        <DialogHeader><DialogTitle>Batch {batch?.local_date} · {batch?.rep_name}</DialogTitle></DialogHeader>
        {data && <><LeadsTable items={data.items} onOpen={setOpen} onFund={setFund} /><Pager page={page} total={data.total} onPage={load} /></>}
        <LeadDetail id={open} onClose={() => setOpen(null)} />
        {fund && <FundDialog lead={fund} open onClose={() => setFund(null)} onDone={() => load(page)} />}
      </DialogContent>
    </Dialog>
  );
}

function TrimConfirm({ total, n, busy, onAll, onTrim, onBack, onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white" data-testid="push-trim-confirm">
        <DialogHeader><DialogTitle>These are fresh leads that must be hit</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-700">You chose to send {n.toLocaleString()} of {total.toLocaleString()}. Do you want to send all {total.toLocaleString()}, or throw the remaining {(total - n).toLocaleString()} back into the pool?</p>
        <p className="text-xs text-zinc-500">Leads you throw back come off your batch now and your limit goes back up, so you can pull more later to finish the blast.</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={onAll} disabled={busy} data-testid="push-send-all-btn">Send all {total.toLocaleString()}</Button>
          <Button variant="outline" onClick={onTrim} disabled={busy} data-testid="push-trim-btn">Send {n.toLocaleString()}, return {(total - n).toLocaleString()} to pool</Button>
          <Button variant="ghost" onClick={onBack} disabled={busy} data-testid="push-trim-back-btn">Back</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PushDialog({ batch, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(batch.lead_count - (batch.funded_count || 0));
  const [confirm, setConfirm] = useState(false);
  const done = batch.transfer?.status === "completed";
  const avail = batch.lead_count - (batch.funded_count || 0);
  const n = Math.max(1, Math.min(avail, Number(count) || 0));
  const send = async (sendCount) => {
    setBusy(true);
    try {
      const r = await api.post(`/batches/${batch.id}/push`, { text_message: text.trim() || null, send_count: sendCount || null });
      toast.success(r.data.note || (done ? "Already in its campaign" : `Pushing ${sendCount || batch.lead_count} leads into "${r.data.campaign_name}"`
        + (r.data.returned ? ` · ${r.data.returned} returned to the pool` : "")));
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const go = () => (!done && n < avail ? setConfirm(true) : send(null));
  if (confirm) return <TrimConfirm total={avail} n={n} busy={busy} onAll={() => send(null)} onTrim={() => send(n)} onBack={() => setConfirm(false)} onClose={onClose} />;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white" data-testid="push-dialog">
        <DialogHeader><DialogTitle>{done ? "Set campaign message" : `Push ${batch.lead_count} lead${batch.lead_count === 1 ? "" : "s"} to Entrance`}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-600">{done ? "These contacts are already in this batch's campaign." : "The leads go into a brand-new campaign in your Entrance, named with your name and today's date. Each batch you push gets its own campaign; a second push today is named with (2), and so on."}</p>
        {!done && <label className="text-xs space-y-1 block"><div>How many of the {avail.toLocaleString()} would you like to send?</div>
          <input type="number" min={1} max={avail} className="fld w-40" value={count} onChange={(e) => setCount(e.target.value)} data-testid="push-count-input" /></label>}
        <label className="text-xs space-y-1 block"><div>Text message (optional, up to 1600 characters)</div>
          <textarea className="fld w-full h-28 py-2" maxLength={1600} value={text} onChange={(e) => setText(e.target.value)} placeholder="Leave empty to write it in Entrance" data-testid="push-message-input" /></label>
        <p className="text-[11px] text-zinc-500">Nothing is sent from here. Open the campaign in Entrance to write or edit the message and send it. Anything not texted by midnight is taken back and the campaign is deleted.</p>
        <Button onClick={go} disabled={busy || (done && !text.trim())} data-testid="push-confirm-btn"><Send className="w-3.5 h-3.5 mr-1" />{done ? "Save message" : "Push to a new campaign"}</Button>
      </DialogContent>
    </Dialog>
  );
}

const SKIP_LABELS = { filtered_by_already_added: "already in campaign", filtered_by_stop: "opted out (STOP)", filtered_by_invalid_number: "invalid number",
  filtered_by_master: "already messaged", filtered_by_channel: "existing conversation", filtered_by_invalid_contact: "invalid contact", filtered_by_contact_error: "error" };

function ImportBreakdown({ imp, id, skipped, showRep }) {
  if (!imp) return null;
  const skips = Object.entries(SKIP_LABELS).filter(([k]) => imp[k] > 0).map(([k, l]) => `${imp[k].toLocaleString()} ${l}`);
  const inCampaign = (imp.added_contacts || 0) + (imp.updated_contacts || 0);
  return (
    <div className="text-[11px] text-zinc-600" data-testid={`push-import-${id}`}>
      Entrance kept {inCampaign.toLocaleString()} of {(imp.total_contacts || 0).toLocaleString()}
      {skips.length > 0 && <span className="text-amber-700"> · skipped {skips.join(", ")}</span>}
      {imp.error_message && <span className="text-red-700"> · {imp.error_message}</span>}
      {skipped > 0 && <div className="text-emerald-700" data-testid={`push-skipped-returned-${id}`}>{skipped.toLocaleString()} skipped leads went back to the pool and back into {showRep ? "the rep's" : "your"} daily allowance</div>}
    </div>
  );
}

function PushProgress({ tr, id, leadCount, showRep }) {
  if (!tr) return null;
  const p = tr.progress;
  const running = ["queued", "processing", "reserving"].includes(tr.status);
  if (!running && tr.status === "completed") {
    const notSent = tr.summary && leadCount && !tr.entrance_skipped ? leadCount - tr.summary.eligible_sent : 0;
    const imp = p && p.stage === "entrance_importing" ? p : null;
    return (
      <div className="mt-1">
        <div className="text-[11px] text-emerald-700" data-testid={`push-done-${id}`}>{tr.summary ? `${(tr.entrance_skipped ? tr.summary.succeeded - tr.entrance_skipped : tr.summary.succeeded).toLocaleString()} ${tr.entrance_skipped ? "in" : "sent to"} Entrance` : "In Entrance"}{tr.campaign_name ? ` · ${tr.campaign_name}` : ""}</div>
        {imp && <div className="w-56 mt-1" data-testid={`push-importing-${id}`}>
          <div className="h-1.5 rounded bg-zinc-200 overflow-hidden"><div className={`h-full bg-[#6cc417] transition-[width] duration-500 ${imp.done ? "" : "animate-pulse w-1/4"}`} style={imp.done ? { width: `${Math.min(100, Math.round((imp.done / (imp.total || 1)) * 100))}%` } : undefined} /></div>
          <div className="text-[11px] text-zinc-500 mt-0.5">Entrance is adding them to the campaign{imp.done ? ` · ${imp.done.toLocaleString()} / ${imp.total.toLocaleString()}` : "…"}</div></div>}
        {notSent > 0 && <div className="text-[11px] text-amber-700" data-testid={`push-not-sent-${id}`}>{notSent.toLocaleString()} not sent (no phone, opted out or expired)</div>}
        <ImportBreakdown imp={tr.entrance_import} id={id} skipped={tr.entrance_skipped} showRep={showRep} />
      </div>
    );
  }
  if (!running) return tr.summary ? <div className="text-[11px] text-red-700 mt-1">{tr.summary.succeeded} of {tr.summary.eligible_sent} sent</div> : null;
  const pct = p && p.total ? Math.min(100, Math.round((p.done / p.total) * 100)) : 0;
  const label = !p ? "Starting…" : p.stage === "entrance_importing" ? `Entrance importing ${p.done.toLocaleString()} / ${p.total.toLocaleString()}` : p.stage === "done" ? "Finishing…" : `Sending ${p.total.toLocaleString()} contacts… ${p.done.toLocaleString()} confirmed`;
  return (
    <div className="mt-1 w-56" data-testid={`push-progress-${id}`}>
      <div className="h-2 rounded bg-zinc-200 overflow-hidden"><div className={`h-full bg-gradient-to-r from-[#3f7d05] to-[#6cc417] transition-[width] duration-500 ${!p || (p.stage === "uploading" && !p.done) ? "animate-pulse w-1/3" : ""}`} style={p && p.done ? { width: `${pct}%` } : undefined} /></div>
      <div className="text-[11px] text-zinc-600 mt-0.5">{label}{p && p.done ? ` · ${pct}%` : ""}</div>
    </div>
  );
}

function RetractDialog({ batch, admin, onClose, onDone }) {
  const avail = batch.lead_count - (batch.funded_count || 0);
  const [keep, setKeep] = useState(admin ? 0 : Math.max(0, avail - 1));
  const [busy, setBusy] = useState(false);
  const k = Math.max(0, Math.min(avail, Number(keep) || 0));
  const pushed = !!batch.transfer;
  const go = async () => {
    setBusy(true);
    try {
      const r = (await api.post(`/batches/${batch.id}/retract`, { keep: k })).data;
      toast.success(`${r.returned.toLocaleString()} back in the pool` + (r.campaign_deleted ? " · campaign deleted" : "")
        + (r.repushed ? ` · ${r.kept.toLocaleString()} re-pushed into "${r.repushed}"` : ""));
      if (r.repush_error) toast.error(`Re-push didn't start: ${typeof r.repush_error === "string" ? r.repush_error : "push again from the inbox"}`);
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white" data-testid="retract-dialog">
        <DialogHeader><DialogTitle>{admin ? `Revoke leads from ${batch.rep_name}` : "Retract and resend"}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-600">{pushed ? "The unsent campaign is deleted in Entrance. " : ""}You keep the same leads you choose below{pushed ? ", re-pushed into a fresh campaign" : ""}; the rest go back into the pool and the limit goes back up. Campaigns that already started texting can't be retracted.</p>
        <label className="text-xs space-y-1 block"><div>How many of the {avail.toLocaleString()} should {admin ? "the rep" : "you"} keep? (0 = all back to the pool)</div>
          <input type="number" min={0} max={avail} className="fld w-40" value={keep} onChange={(e) => setKeep(e.target.value)} data-testid="retract-keep-input" /></label>
        <div className="text-xs text-zinc-500" data-testid="retract-summary">Keep {k.toLocaleString()} · return {(avail - k).toLocaleString()} to the pool</div>
        <Button onClick={go} disabled={busy || (!pushed && k >= avail)} className="self-start" data-testid="retract-confirm-btn"><Undo2 className="w-3.5 h-3.5 mr-1" />{admin ? "Revoke" : "Retract"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function BatchInbox({ batches, reload, showRep, onPushed, retract }) {
  const [view, setView] = useState(null);
  const [pushing, setPushing] = useState(null);
  const [retracting, setRetracting] = useState(null);
  const active = (batches || []).some((b) => ["queued", "processing", "reserving"].includes(b.transfer?.status)
    || (b.transfer?.status === "completed" && b.transfer?.progress?.stage === "entrance_importing"));
  useEffect(() => {
    if (!active) { return undefined; }
    const t = setInterval(reload, 2500);
    return () => clearInterval(t);
  }, [active, reload]);
  if (!batches) return null;
  if (!batches.length) return <Empty text="No lead batches yet. Each blast you request shows up here." />;
  const groups = {};
  batches.forEach((b) => { (groups[b.local_date] = groups[b.local_date] || []).push(b); });
  const push = (b) => setPushing(b);
  return (
    <div className="space-y-6" data-testid="batch-inbox">
      {Object.keys(groups).sort().reverse().map((date) => (
        <section key={date} className="fade-in" data-testid={`batch-section-${date}`}>
          <div className="flex items-baseline gap-3 mb-2">
            <h3 className="font-bold text-zinc-900">{new Date(date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" })}</h3>
            <span className="mono text-xs text-zinc-500">{(() => { const n = groups[date].reduce((a, b) => a + b.lead_count, 0); return `${n} lead${n === 1 ? "" : "s"}`; })()}</span>
          </div>
          <div className="panel overflow-x-auto">
            <table className="dt"><thead><tr><th>Allocated</th>{showRep && <th>Rep</th>}<th className="num">Leads</th><th className="num">Funded</th><th>Entrance</th><th>Expires</th><th>Remaining</th><th /></tr></thead>
              <tbody>{groups[date].map((b) => (
                <tr key={b.id} data-testid={`batch-row-${b.id}`}>
                  <td className="mono text-xs">{fmtDT(b.allocated_at)}{b.account_name && <div className="font-sans text-[11px] font-semibold text-[#3f7d05]" data-testid={`batch-account-${b.id}`}>{b.account_name}</div>}</td>{showRep && <td>{b.rep_name}</td>}
                  <td className="num">{b.lead_count}</td><td className="num">{b.funded_count}</td>
                  <td className="min-w-[14rem]"><StatusBadge s={b.transfer?.status || b.transfer_status} testid={`batch-transfer-status-${b.id}`} /><PushProgress tr={b.transfer} id={b.id} leadCount={b.lead_count - (b.funded_count || 0)} showRep={showRep} />{b.day_end && <div className="text-[11px] text-amber-700" data-testid={`batch-day-end-${b.id}`}>{b.day_end.returned.toLocaleString()} not texted by midnight, back in rotation{b.day_end.campaign_deleted ? " · campaign deleted" : ""}</div>}</td>
                  <td className="mono text-xs">{fmtDT(b.expires_at)}</td><td><Countdown seconds={b.remaining_seconds} /></td>
                  <td className="text-right whitespace-nowrap space-x-1">
                    {!b.expired && <Button size="sm" variant="outline" onClick={() => setView(b)} data-testid={`batch-view-${b.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Leads</Button>}
                    {b.expired && <span className="text-xs text-zinc-500">Expired — details closed</span>}
                    {!b.expired && <Button size="sm" onClick={() => push(b)} disabled={["queued", "processing"].includes(b.transfer?.status)} data-testid={`batch-push-${b.id}`}><Send className="w-3.5 h-3.5 mr-1" />{b.transfer?.status === "completed" ? "Set message" : "Push to Entrance"}</Button>}
                    {!b.expired && retract && <Button size="sm" variant="outline" onClick={() => setRetracting(b)} disabled={["reserving", "queued", "processing"].includes(b.transfer?.status)} data-testid={`batch-retract-${b.id}`}><Undo2 className="w-3.5 h-3.5 mr-1" />{retract === "admin" ? "Revoke" : "Retract"}</Button>}
                  </td>
                </tr>))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
      {view && <BatchLeads batch={view} onClose={() => { setView(null); reload(); }} />}
      {pushing && <PushDialog batch={pushing} onClose={() => setPushing(null)} onDone={onPushed || reload} />}
      {retracting && <RetractDialog batch={retracting} admin={retract === "admin"} onClose={() => setRetracting(null)} onDone={onPushed || reload} />}
    </div>
  );
}
